import type { MouseEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Trans,
  splitLocalePath,
  useLocalePath,
  useTranslation,
} from "@thyra/landing-i18n";
import { animateScroll, scroller } from "react-scroll";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeLogo } from "./ThemeLogo";
import { HoverCard } from "./ui/TooltipCard";
import { SocialIcon } from "./SocialIcon";
import { FooterAgenticCta } from "./FooterAgenticCta";

const SECTION_SCROLL_OPTIONS = { smooth: true, duration: 650, offset: -88 };

const COPYRIGHT_YEAR = 2026;

const SOCIAL_LINKS = [
  {
    href: "https://www.linkedin.com/company/111886268",
    label: "LinkedIn",
    icon: "linkedin",
  },
  {
    href: "https://www.instagram.com/thyraai/",
    label: "Instagram",
    icon: "instagram",
  },
  {
    href: "https://x.com/thyra_ai",
    label: "X",
    icon: "x",
  },
] as const;

/** Horizontal wordmark: dark text on light UI, white on dark UI (CSS-driven, matches ThemeLogo pattern). */
function DigitalOceanFooterLogo() {
  return (
    <>
      <img
        src="/digitalocean/dark.svg"
        alt="DigitalOcean"
        className="h-7 w-auto max-w-[200px] opacity-90 sm:h-8 dark:hidden"
        width={158}
        height={30}
        loading="lazy"
      />
      <img
        src="/digitalocean/white.svg"
        alt="DigitalOcean"
        className="hidden h-7 w-auto max-w-[200px] opacity-90 sm:h-8 dark:block"
        width={158}
        height={30}
        loading="lazy"
      />
    </>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 17L17 7" />
      <path d="M7 7h10v10" />
    </svg>
  );
}

export function Footer({ hideCta = false }: { hideCta?: boolean } = {}) {
  const { t } = useTranslation("sections");
  const lp = useLocalePath();
  const location = useLocation();
  const isHome = splitLocalePath(location.pathname).path === "/";

  const handleHomeClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (isHome) {
      e.preventDefault();
      animateScroll.scrollToTop({ smooth: true, duration: 500 });
    }
  };

  /** On home: smooth-scroll in place. Else navigate to `/#id` so ScrollToTop can scroll after landing. */
  const handleSectionNav = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    if (isHome) {
      e.preventDefault();
      scroller.scrollTo(id, SECTION_SCROLL_OPTIONS);
    }
  };

  return (
    <footer
      className={`w-full px-4 sm:px-6 ${
        isHome ? "bg-background pb-8 sm:pb-10" : "pb-6 sm:pb-10 pt-4"
      }`}
    >
      <div
        className={`mx-auto w-full max-w-7xl ${
          isHome ? "flex flex-col gap-4 sm:gap-5" : ""
        }`}
      >
        {/* Home closes with the blue CTA band (CTASection); skip the legacy footer CTA there. */}
        {isHome && !hideCta ? <FooterAgenticCta /> : null}
        <div className="w-full overflow-hidden rounded-3xl border border-border bg-card">
          <div
            className={`w-full max-w-full px-4 pb-6 text-left sm:px-8 sm:pb-8 ${
              isHome ? "pt-8 sm:pt-10" : "pt-12 sm:pt-20"
            }`}
          >
            <div className="flex w-full min-w-0 flex-col gap-12 md:flex-row md:items-start md:gap-12 lg:gap-16">
              <div className="flex w-full min-w-0 flex-col items-start md:max-w-md lg:max-w-lg">
                <Link
                  to={lp("/")}
                  onClick={handleHomeClick}
                  className="inline-flex items-center text-foreground"
                  aria-label={t("footer.homeAriaLabel")}
                >
                  <ThemeLogo height={28} className="h-7 w-auto" />
                </Link>
                <p className="text-muted-foreground text-sm mt-2">
                  Thyra AI Corporation
                </p>
                <h2 className="text-xl sm:text-2xl font-medium text-foreground mt-6 max-w-md">
                  {t("footer.tagline")}
                </h2>
              </div>
              <div className="grid w-full min-w-0 grid-cols-2 gap-x-6 gap-y-8 sm:gap-8 md:flex-1 md:grid-cols-4 md:gap-8">
                <div className="space-y-4">
                  <h3 className="font-semibold text-foreground">
                    {t("footer.columns.pages")}
                  </h3>
                  <ul className="space-y-3">
                    <li>
                      <Link
                        to={lp("/#product")}
                        onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                          handleSectionNav(e, "product")
                        }
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t("footer.links.features")}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={lp("/#pricing")}
                        onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                          handleSectionNav(e, "pricing")
                        }
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t("footer.links.pricing")}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={lp("/business")}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t("footer.links.forBusiness")}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={lp("/about")}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t("footer.links.about")}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={lp("/team")}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t("footer.links.team")}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={lp("/blog")}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t("footer.links.blog")}
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold text-foreground">
                    {t("footer.columns.support")}
                  </h3>
                  <ul className="space-y-3">
                    <li>
                      <Link
                        to={lp("/contact")}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t("footer.links.contact")}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={lp("/#faq")}
                        onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                          handleSectionNav(e, "faq")
                        }
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t("footer.links.faq")}
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold text-foreground">
                    {t("footer.columns.socials")}
                  </h3>
                  <ul className="space-y-3">
                    {SOCIAL_LINKS.map(({ href, label }) => (
                      <li key={label}>
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                        >
                          <span>{label}</span>
                          <ExternalLinkIcon />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold text-foreground">
                    {t("footer.columns.legal")}
                  </h3>
                  <ul className="space-y-3">
                    <li>
                      <Link
                        to={lp("/privacy")}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t("footer.links.privacy")}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={lp("/terms")}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t("footer.links.terms")}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={lp("/cookies")}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t("footer.links.cookies")}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="mt-10 h-px w-full bg-border/50" aria-hidden />
            <div
              className={`flex w-full min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between ${
                isHome ? "p-6 sm:p-8" : "p-8 sm:p-10"
              }`}
            >
              <div className="w-full min-w-0 text-sm text-muted-foreground space-y-1 sm:max-w-4xl">
                <p>{t("footer.copyright", { year: COPYRIGHT_YEAR })}</p>
                <p>{t("footer.registration")}</p>
              </div>
              <div className="flex w-full shrink-0 items-center gap-4 sm:w-auto">
                <div className="flex items-center gap-2">
                  <LanguageSwitcher placement="top" />
                  <ThemeSwitcher />
                </div>
                {SOCIAL_LINKS.map(({ href, label, icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={label}
                  >
                    <SocialIcon icon={icon} />
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-4 h-px w-full bg-border/50" aria-hidden />
            <div className="flex flex-col items-center justify-center gap-2 pt-6 pb-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t("footer.supportedBy")}
              </p>
              <HoverCard
                interactive
                containerClassName="!max-w-[min(92vw,300px)] !p-0 shadow-xl"
                triggerClassName="cursor-help"
                content={
                  <div className="flex flex-col gap-2 p-3">
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      <Trans
                        t={t}
                        i18nKey="footer.hatch.description"
                        components={{
                          hatchLink: (
                            <a
                              href="https://www.digitalocean.com/startups"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-foreground underline underline-offset-2 hover:text-primary"
                            />
                          ),
                        }}
                      />
                    </p>
                  </div>
                }
              >
                <a
                  href="https://www.digitalocean.com/startups"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block origin-center rounded-md p-1 transition-transform duration-300 ease-out hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label={t("footer.hatch.ariaLabel")}
                >
                  <DigitalOceanFooterLogo />
                </a>
              </HoverCard>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
